import { NgClass } from '@angular/common'
import type { OnInit } from '@angular/core'
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  inject,
  input,
} from '@angular/core'
import type { ControlValueAccessor } from '@angular/forms'
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms'

export interface SelectOption {
  value: string
  label: string
  disabled: boolean
}

@Component({
  selector: 'dhp-select-input',
  templateUrl: './select-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: SelectInputComponent,
      multi: true,
    },
  ],
  imports: [FormsModule, NgClass],
})
export class SelectInputComponent implements ControlValueAccessor, OnInit {
  private host = inject(ElementRef)

  private _value: string | null = null
  private isFormControlled = false
  private allowedForwardClasses = [
    'select-disabled',
    'select-hover',
    'select-active',
    'select-focus',
  ]

  private onChange: CallbackFunction = () => {}

  private onTouched: CallbackFunction = () => {}

  forwardedClasses: string[] = []

  readonly options = input<SelectOption[]>([])
  readonly required = input<boolean>(false)

  // Disabled and value should only be used if you do not use reactive forms
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() disabled = false
  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input()
  set value(val: string | null) {
    if (!this.isFormControlled) {
      this._value = val
    }
  }

  get value(): string | null {
    return this._value
  }

  ngOnInit(): void {
    const classList = this.host.nativeElement.classList as DOMTokenList
    this.forwardedClasses = this.allowedForwardClasses.filter(cls =>
      classList.contains(cls)
    )
  }

  writeValue(value: string | null): void {
    // This is only used by the form logic, so we use this to determine that
    this.isFormControlled = true
    this._value = value
  }

  registerOnChange(fn: (value?: string) => void): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled
  }

  public handleChange(event: Event): void {
    const select = event.target as HTMLSelectElement
    this.value = select.value
    this.onChange(select.value)
    this.handleTouched()
  }

  public handleTouched(): void {
    this.onTouched()
  }
}

type CallbackFunction = (value?: string) => void
